/**
 * Category Help Modal for Personal Gap Analysis
 * Displays detailed information about a specific category
 */
import React from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import { PERSONAL_CATEGORY_DESCRIPTIONS, PERSONAL_CATEGORY_KEY_MAP } from '../../utils/categoryDescriptions';

const CategoryHelpModal = ({
  show,
  categoryKey,
  onClose
}) => {
  if (!show || !categoryKey) return null;

  const fullCategoryName = PERSONAL_CATEGORY_KEY_MAP[categoryKey] || categoryKey;
  const categoryDesc = PERSONAL_CATEGORY_DESCRIPTIONS[fullCategoryName];

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-2 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-indigo-700 text-white rounded-t-lg">
          <h2 className="text-2xl font-bold">{fullCategoryName}</h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {!categoryDesc ? (
            <div className="text-gray-700">
              <p>Category information not available.</p>
            </div>
          ) : (
            <>
              {/* Summary */}
              <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded">
                <h3 className="font-bold text-purple-900 mb-2">Summary</h3>
                <p className="text-gray-700 leading-relaxed">
                  {categoryDesc.summary}
                </p>
              </div>

              {/* Detailed Explanation */}
              <div>
                <h3 className="font-bold text-gray-900 mb-3 text-lg">What This Category Measures</h3>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {categoryDesc.detailed}
                </p>
              </div>

              {/* Key Questions */}
              {categoryDesc.keyQuestions && categoryDesc.keyQuestions.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-gray-900 mb-3">Key Questions to Consider</h3>
                  <ul className="space-y-2">
                    {categoryDesc.keyQuestions.map((question, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <span className="text-purple-600 font-bold mt-0.5">•</span>
                        <span className="text-gray-700">{question}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Why This Matters */}
              <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded">
                <h4 className="font-bold text-purple-900 mb-2">💡 Why This Matters</h4>
                <p className="text-sm text-purple-800 leading-relaxed">
                  This personal assessment category evaluates a critical aspect of your readiness for life after business exit. Improving your score in this area can significantly enhance your personal fulfillment, relationships, and overall well-being in your post-exit life.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-semibold min-h-[44px]"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
};

CategoryHelpModal.propTypes = {
  show: PropTypes.bool.isRequired,
  categoryKey: PropTypes.string,
  onClose: PropTypes.func.isRequired
};

export default CategoryHelpModal;
